import type { ReportDetailed } from '@/api/generated/model';
import type { ReportDetailsUpdateValues } from '@/domains/reports/hooks/use-update-report-details';
import { formatDateAsTimeFromNowCompact } from '@/utils/date.util';
import { isDefaultIdReadOnlyReport, isSystemReport } from '@/domains/reports/utils/system-report.utils';

type ReportTimestampsLike = {
  updated_at?: string | null;
  updated_by?: string | null;
  updated_by_full_name?: string | null;
  created_at?: string | null;
  created_by?: string | null;
};

export type { ReportTimestampsLike };

export const REPORT_IDENTITY_TITLE_MAX_LENGTH = 80;
export const REPORT_IDENTITY_DESCRIPTION_MAX_LENGTH = 280;

export function buildReportIdentityUpdatePayload(
  report: ReportDetailed,
  patch: Partial<ReportDetailsUpdateValues>,
): ReportDetailsUpdateValues {
  return {
    name: report.name,
    description: report.description ?? '',
    icon: report.icon ?? null,
    ...patch,
  };
}

export function formatReportEditorName(email: string | null | undefined): string {
  const value = (email ?? '').trim();
  if (!value) return 'Unknown';
  const atIndex = value.indexOf('@');
  if (atIndex <= 0) return value;
  return value.slice(0, atIndex);
}

export function getDisplayNameInitials(displayName: string): string {
  const trimmed = displayName.trim();
  if (!trimmed || trimmed === 'Unknown') return '?';

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase().slice(0, 2);
  }

  return trimmed.slice(0, 2).toUpperCase();
}

export function getReportEditorInitials(email: string | null | undefined): string {
  const editor = formatReportEditorName(email);
  if (editor === 'Unknown') return '?';
  return getDisplayNameInitials(editor.replace(/[._-]+/g, ' '));
}

export type ReportEditedMeta = {
  timeAgo: string;
  editorName: string;
  editorInitials: string;
  editorEmail: string | null;
};

export function getReportEditedMeta(report: ReportTimestampsLike): ReportEditedMeta | null {
  const editedAt = getReportLastEditedAt(report);
  if (!editedAt) return null;

  const editorEmail = getReportLastEditedBy(report);
  const editorName = report.updated_by_full_name ?? formatReportEditorName(editorEmail);
  const editorInitials = report.updated_by_full_name
    ? getDisplayNameInitials(report.updated_by_full_name)
    : getReportEditorInitials(editorEmail);
  return {
    timeAgo: formatDateAsTimeFromNowCompact(editedAt),
    editorName,
    editorInitials,
    editorEmail,
  };
}

export function getReportLastEditedAt(report: ReportTimestampsLike): string | null {
  return report.updated_at ?? report.created_at ?? null;
}

export function getReportLastEditedBy(report: ReportTimestampsLike): string | null {
  return report.updated_by ?? report.created_by ?? null;
}

export function formatReportEditedCaption(report: ReportTimestampsLike): string | null {
  const meta = getReportEditedMeta(report);
  if (!meta) return null;

  return `Edited ${meta.timeAgo} by ${meta.editorName}`;
}

export function isReportIdentitySystemReport(report: ReportDetailed): boolean {
  return isDefaultIdReadOnlyReport(report) || isSystemReport(report);
}

export function canEditReportIdentity(params: {
  report: ReportDetailed | undefined;
  canWriteReports: boolean;
  isCompanyAdmin: boolean;
  isReportOwner: boolean;
  isSharedLink: boolean;
}): boolean {
  const { report, canWriteReports, isCompanyAdmin, isReportOwner, isSharedLink } = params;
  if (!report || isSharedLink || isReportIdentitySystemReport(report)) return false;
  if (isReportOwner) return canWriteReports;
  return isCompanyAdmin && !report.is_private && canWriteReports;
}

export function normalizeReportTitleForSave(value: string, previousValue: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) return previousValue;
  return trimmed.slice(0, REPORT_IDENTITY_TITLE_MAX_LENGTH);
}

export function normalizeReportDescriptionForSave(value: string): string {
  return value.trim().slice(0, REPORT_IDENTITY_DESCRIPTION_MAX_LENGTH);
}
